import { NgModule, isDevMode, APP_INITIALIZER } from '@angular/core';
import { BrowserModule } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { HttpClientModule, HTTP_INTERCEPTORS } from '@angular/common/http';
import { FormsModule } from '@angular/forms';
import { HashLocationStrategy, LocationStrategy } from '@angular/common';
import { ServiceWorkerModule } from '@angular/service-worker';

// PWA Components
import { AppComponent } from './app.component';
import { MobileFrequenciaComponent } from '@features/frequencia/components/mobile-frequencia/mobile-frequencia.component';
import { MobileFrequenciaAlunoCardDiaComponent } from '@features/frequencia/components/mobile-frequencia/mobile-frequencia-aluno-card-dia.component';
import { MobileContextBarComponent } from '@core/context/components/mobile-context-bar.component';
import { InstallPromptComponent } from '@infrastructure/pwa/components/install-prompt.component';
import { UserProfileModalComponent } from '@infrastructure/ui/components/modals/user-profile-modal/user-profile-modal.component';
import { ContextSelectorModalComponent } from '@infrastructure/ui/components/modals/context-selector-modal/context-selector-modal.component';
import { SettingsModalComponent } from '@infrastructure/ui/components/modals/settings-modal/settings-modal.component';
import { NotificationToastComponent } from '@infrastructure/ui/components/feedback/notification-toast/notification-toast.component';
import { LoadingModalComponent } from '@infrastructure/ui/components/modals/loading-modal/loading-modal.component';
import { SemPermissaoModalComponent } from '@infrastructure/ui/components/modals/sem-permissao-modal/sem-permissao-modal.component';
import { SemPermissaoPageComponent } from '@infrastructure/ui/components/pages/sem-permissao-page/sem-permissao-page.component';
import { PermissionsModule } from '@shared/permissions.module';
import { ContextCancelAlertComponent } from '@shared/components/context-cancel-alert.component';
import { ContextCardComponent } from '@infrastructure/ui/components/context-card/context-card.component';
import { PeriodoSelectComponent } from '@infrastructure/ui/components/forms/periodo-select/periodo-select.component';
import { HierarquiaSelectComponent } from '@infrastructure/ui/components/hierarquia-select/hierarquia-select.component';
import { ComponentesCurricularesSelectComponent } from '@infrastructure/ui/components/modals/context-selector-modal/componentes-curriculares-select.component';
import { PageTitleModule } from '@infrastructure/ui/components/page-title/page-title.module';
import { SidebarComponent } from '@infrastructure/ui/components/sidebar/sidebar.component';
import { SharedModule } from '@shared/shared.module';

// PWA Services
import { MobileFrequenciaService } from '@features/frequencia/services/mobile-frequencia.service';
import { MobileContextService } from '@core/context/services/mobile-context.service';
import { AuthInterceptorService } from '@core/auth/services/auth-interceptor.service';
import { AuthErrorInterceptorService } from '@core/auth/services/auth-error-interceptor.service';
import { HttpErrorNotificationInterceptor } from '@core/error-handling/services/http-error-notification.interceptor';
import { ContextRecoveryInterceptor } from '@core/error-handling/services/context-recovery-interceptor.service';
import { StandaloneAuthService } from '@core/auth/services/standalone-auth.service';
import { TokenService } from '@core/auth/services/token.service';
import { ContextService } from '@core/context/services/context.service';
import { ErrorHandlerService } from '@core/error-handling/services/error-handler.service';
import { ConfigService } from '@core/config/services/config.service';
import { FrequenciaComponentsModule } from '@features/frequencia/components/frequencia-components.module';
import { DesempenhoEscolarModule } from '@features/desempenho-escolar/components/desempenho-escolar.module';
import { InstrumentosAvaliacaoModule } from '@features/instrumentos-avaliacao/components/instrumentos-avaliacao.module';
import { AcompanhamentoPedagogicoModule } from '@features/acompanhamento-pedagogico/components/acompanhamento-pedagogico.module';
import { PlanejamentoAulaModule } from '@features/planejamento-aula/components/planejamento-aula.module';
import { PlanoEnsinoModule } from '@features/plano-ensino/components/plano-ensino.module';
import { AppRoutingModule } from './app-routing.module';
import {
  LucideAngularModule,
  X,
  BookOpen,
  BookCheck,
  BookOpenCheck,
  ClipboardCheck,
  CheckSquare,
  Square,
  SquareCheckBig,
  BarChart3,
  Menu,
  User,
  ChevronDown,
  ChevronUp,
  Calendar,
  CalendarDays,
  Search,
  List,
  Calculator,
  Eye,
  RefreshCw,
  ArrowLeftRight,
  Undo,
  ArrowLeftCircle,
  ArrowRightCircle,
  Loader2,
  AlertTriangle,
  Users,
  ClipboardList,
  MessageSquare,
  MessageCircleMore,
  ReplyAll,
  Star,
  Info,
  CalendarX,
  CalendarX2,
  Target,
  Paperclip,
  Trash2,
  Plus,
  Key,
  LogOut,
  Lock,
  LockOpen,
  ChevronLeft,
  ChevronRight,
  ArrowUpDown,
  ArrowUpAZ,
  ArrowDownZA,
  ArrowUp01,
  ArrowDown10,
  ArrowDown01,
  Files,
  FileText,
  Hash,
  ArrowDownAZ,
  Settings,
  Sun,
  Moon,
  Monitor,
  Palette,
  CheckCircle,
  Circle,
  CircleSlash,
  RotateCcw,
  Home,
  Check,
  AlertCircle,
  PlusCircle,
  Edit2,
  Ban
} from 'lucide-angular';


@NgModule({
  declarations: [
    AppComponent,
    MobileFrequenciaComponent,
    MobileFrequenciaAlunoCardDiaComponent,
    MobileContextBarComponent,
    InstallPromptComponent,
    UserProfileModalComponent,
    ContextSelectorModalComponent,
    SettingsModalComponent,
    NotificationToastComponent,
    LoadingModalComponent,
    SemPermissaoModalComponent,
    SemPermissaoPageComponent,
    ContextCancelAlertComponent,
    ContextCardComponent,
    SidebarComponent
  ],
  imports: [
    BrowserModule,
    BrowserAnimationsModule,
    HttpClientModule,
    FormsModule,
    AppRoutingModule,
    FrequenciaComponentsModule,
    DesempenhoEscolarModule,
    InstrumentosAvaliacaoModule,
    AcompanhamentoPedagogicoModule,
    PlanejamentoAulaModule,
    PlanoEnsinoModule,
    PermissionsModule,
    PeriodoSelectComponent,
    HierarquiaSelectComponent,
    ComponentesCurricularesSelectComponent,
    PageTitleModule,
    SharedModule,
    LucideAngularModule.pick({
      X,
      BookOpen,
      BookOpenCheck,
      BookCheck,
      ClipboardCheck,
      CheckSquare,
      Square,
      SquareCheckBig,
      BarChart3,
      Menu,
      User,
      ChevronDown,
      ChevronUp,
      Calendar,
      CalendarDays,
      Search,
      List,
      Calculator,
      Eye,
      RefreshCw,
      ArrowLeftRight,
      Undo,
      ArrowLeftCircle,
      ArrowRightCircle,
      Loader2,
      AlertTriangle,
      Users,
      ClipboardList,
      MessageSquare,
      MessageCircleMore,
      ReplyAll,
      Star,
      Info,
      CalendarX,
      CalendarX2,
      Target,
      Paperclip,
      Trash2,
      Plus,
      Key,
      LogOut,
      Lock,
      LockOpen,
      ChevronLeft,
      ChevronRight,
      ArrowUpDown,
      ArrowUpAZ,
      ArrowDownZA,
      ArrowUp01,
      ArrowDown10,
      ArrowDown01,
      Files,
      FileText,
      Hash,
      ArrowDownAZ,
      Settings,
      Sun,
      Moon,
      Monitor,
      Palette,
      CheckCircle,
      Circle,
      CircleSlash,
      RotateCcw,
      Home,
      Check,
      AlertCircle,
      PlusCircle,
      Edit2,
      Ban
    }),
    ServiceWorkerModule.register('ngsw-worker.js', {
      enabled: !isDevMode(),
      registrationStrategy: 'registerWhenStable:30000'
    })
  ],
  providers: [
    { provide: LocationStrategy, useClass: HashLocationStrategy },
    // Seta a flag de bypass ANTES do Router iniciar. Sem isso, pageMappingGuard roda sem
    // token ainda, toma 401 e AuthErrorInterceptor redireciona a página inteira antes da
    // troca de code por token terminar, causando loop infinito de login (PROF-13326).
    {
      provide: APP_INITIALIZER,
      useFactory: () => () => {
        try {
          const params = new URLSearchParams(window.location.search);
          if (params.get('code')) {
            sessionStorage.setItem('__pwa_post_login_bypass', 'true');
          }
        } catch (e) {
          // Silenciar — não bloquear bootstrap por erro de extração
        }
      },
      multi: true
    },
    // Interceptors executam na ordem de registro: token primeiro, depois erros 401
    { provide: HTTP_INTERCEPTORS, useClass: AuthInterceptorService, multi: true },
    { provide: HTTP_INTERCEPTORS, useClass: AuthErrorInterceptorService, multi: true },
    { provide: HTTP_INTERCEPTORS, useClass: ContextRecoveryInterceptor, multi: true },
    { provide: HTTP_INTERCEPTORS, useClass: HttpErrorNotificationInterceptor, multi: true },
    MobileFrequenciaService,
    MobileContextService,
    TokenService,
    ContextService,
    ErrorHandlerService,
    ConfigService,
    StandaloneAuthService
  ],
  bootstrap: [AppComponent]
})
export class AppModule {}